import { useState, useRef, useEffect } from 'react';
import handleToUserCenter from '@/utils/userCenter';
import errorCode from '@/constants/errorCode';
import { likeThread, unlikeThread } from '@/api/detail';
import { collectThread, uncollectThread } from '@/api/common';
import { jsBridgeCallHandler, pageEntryEnv } from '@/utils/pageEntry';
import { MetricTypes, statusTypes, SlsData } from '@/hooks/useSls';

export enum USER_ACTION_TYPE {
  /** 点赞 */
  PRAISE = 'praise',
  /** 收藏 */
  COLLECT = 'favorite',
}

export const USER_ACTION_LOTTIE_URL = {
  /** 点赞动画url */
  PRAISE: 'https://static.djicdn.com/dji-bbs/v1/_next/static/scripts/thumbup.lottie',
  /** 收藏动画url */
  COLLECT: 'https://static.djicdn.com/dji-bbs/v1/_next/static/scripts/favorite.lottie',
}

export interface ICommonUserAction {
  /** 扩展类名 */
  className?: string;
  /** 用户操作类型 */
  type: USER_ACTION_TYPE;
  /** 操作按钮状态 */
  status: boolean;
  /** 操作统计数量 */
  count?: number;
  /** 帖子 ID */
  tid: string | number;
  /** 帖子标题 */
  title: string;
  /** 用户是否登录 */
  isLoggin: boolean;
  /** 自定义行为回调函数 */
  customCb?: (action: USER_ACTION_TYPE) => void;
  /** 埋点回调函数 */
  trackCb?: (action: USER_ACTION_TYPE, isSelected: boolean, isSuccess: boolean, failReason?: string) => void;
  onReportSlsData?: (data: Array<SlsData>) => void;
}

export interface IPCUserAction extends ICommonUserAction {
  /** 操作按钮及文字尺寸 */
  size?: 'sm' | 'md' | 'lg';
}

export interface IMUserAction extends ICommonUserAction {
  /** 操作按钮和文字的布局方向 */
  direction?: 'hor' | 'ver';
}

export type UserActionType = Pick<
  ICommonUserAction,
  'tid' | 'title' | 'status' | 'count' | 'isLoggin' | 'customCb' | 'trackCb' | 'onReportSlsData'
>

/**
 * 用户行为（点赞、收藏）操作处理
 * @param status 操作按钮状态
 * @param tid 帖子 ID
 * @param title 帖子标题
 * @param count 操作统计数量
 * @param isLoggin 用户是否登录
 * @param customCb 自定义行为回调函数
 * @param trackCb 埋点回调函数
 * @returns { selected: boolean, cnt: number, handleUserAction: (type: USER_ACTION_TYPE) => void }
 */
const useUserAction = ({
  status, tid, title,
  count, isLoggin,
  customCb = () => {},
  /* trackCb不管是否登录都会调用 */
  trackCb = () => {},
  onReportSlsData,
}: UserActionType): any => {
  const [selected, setSelected] = useState(!!status);
  const [cnt, setCnt] = useState(count || 0);
  const [showAnimation, setShowAnimation] = useState(false);
  const animationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setSelected(!!status);
    setCnt(count || 0);
  }, [status, count]);

  useEffect(() => {
    return () => {
      if (animationTimerRef.current) {
        clearTimeout(animationTimerRef.current);
        animationTimerRef.current = null;
      }
    };
  }, []);

  const handlePraise = (action: USER_ACTION_TYPE, isPraised: boolean) => {
    const func = isPraised ? unlikeThread : likeThread;
    if (!isLoggin) {
      trackCb(action, isPraised, false, '未登录');
      return ;
    }
    onReportSlsData?.([
      ['reportMetric', MetricTypes.likeStatus, statusTypes.click],
    ])
    func({ tid }).then(() => {
      trackCb(action, isPraised, true);
      onReportSlsData?.([
        ['reportMetric', MetricTypes.likeStatus, statusTypes.success],
      ])
    })
    .catch((e) => {
      trackCb(action, isPraised, false, e);
      onReportSlsData?.([
        ['reportMetric', MetricTypes.likeStatus, statusTypes.fail],
        ['setEnvironment',{
          [MetricTypes.failReason]: e?.message || e?.code || '未知原因',
        }],
      ])
    });
  };

  const handleCollect = (action: USER_ACTION_TYPE, isCollected: boolean) => {
    const func = isCollected ? uncollectThread : collectThread;
    if (!isLoggin) {
      trackCb(action, isCollected, false, '未登录');
      return ;
    }
    onReportSlsData?.([
      ['reportMetric', MetricTypes.collectStatus, statusTypes.click],
    ])
    func({ id: tid, idtype: 'tid', title }).then(() => {
      trackCb(action, isCollected, true);
      onReportSlsData?.([
        ['reportMetric', MetricTypes.collectStatus, statusTypes.success],
      ])
    })
    .catch((e) => {
      trackCb(action, isCollected, false, e);
      onReportSlsData?.([
        ['reportMetric', MetricTypes.collectStatus, statusTypes.fail],
        ['setEnvironment',{
          [MetricTypes.failReason]: e?.message || e?.code || '未知原因',
        }],
      ])
    });
  };

  const handleUserAction = (action: USER_ACTION_TYPE) => {
    if (action === USER_ACTION_TYPE.PRAISE) {
      handlePraise(action, selected);
    } else if (action === USER_ACTION_TYPE.COLLECT) {
      handleCollect(action, selected);
    }

    if (!isLoggin) {
      window.open(handleToUserCenter(errorCode.UNAUTHORIZED, window.location.href), 'blank');
      return;
    }

    customCb(action);
    setSelected(!selected);
    setCnt(!selected ? cnt + 1 : cnt - 1);
    if (!selected) {
      if (animationTimerRef.current) {
        clearTimeout(animationTimerRef.current);
      }
      setShowAnimation(true);
      animationTimerRef.current = setTimeout(() => {
        setShowAnimation(false);
        animationTimerRef.current = null;
      }, 1800);
    }

    // 对于点赞的操作，如果是在商城 APP 里面需要，上报给商城 APP
    if(action === USER_ACTION_TYPE.PRAISE){
      // tid：帖子id 
      // praised：更新后的点赞状态
      // praise：更新后的点赞数
      const params = {
        tid,
        praised: !selected ? '1' : '0',
        praise: !selected ? cnt + 1 : cnt - 1
      }

      const ua = window.navigator.userAgent;
      const { isAppstoreWebH5 } = pageEntryEnv(ua);
      if(isAppstoreWebH5){
        jsBridgeCallHandler('updateThreadPraised', params);
      }
    }

  };

  const onComplete = () => {
    if (animationTimerRef.current) {
      clearTimeout(animationTimerRef.current);
      animationTimerRef.current = null;
    }
    setShowAnimation(false);
  };

  return {
    selected,
    cnt,
    showAnimation,
    handleUserAction,
    onComplete,
  };
};

export {
  useUserAction,
}
